import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Info, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import {
  statistik,
  type StatZeitraum,
  type StatSeite,
  type StatMerkmalArt,
  type StatKennzahlen,
} from '../api';
import StatistikEinrichtung, { EinbauHinweis } from './StatistikEinrichtung';
import StatistikAbgleich from './StatistikAbgleich';
import { Tagesverlauf, Rangbalken, Lesetreppe } from './StatistikDiagramme';
import StatistikExport from './StatistikExport';

/**
 * Die Reichweite einer Webseite.
 *
 * ══ WAS HIER NICHT STEHT UND WARUM ═════════════════════════════════════════
 *
 * Keine „Besucher", keine Wiederkehrer, keine Wege ueber mehrere Besuche. Die
 * Erhebung ist cookielos; nach Mitternacht ist niemand mehr wiedererkennbar.
 * Deshalb heisst die Kennzahl **Tagesbesuche** und nicht „Besucher", und
 * deshalb steht der Hinweis fest im Kopf und nicht in einem Ausklapper: wer die
 * Zahl in eine Folie kopiert, soll die Einschraenkung mitkopieren.
 *
 * Das gilt in beiden Sprachen. Eine Uebersetzung, die daraus „visitors" machte,
 * waere ein inhaltlicher Fehler und keine Geschmacksfrage — die Texte stehen
 * deshalb in `locales/`, wo beide Fassungen nebeneinander liegen.
 */

const ZEITRAEUME: { wert: StatZeitraum; schluessel: string }[] = [
  { wert: 'heute', schluessel: 'today' },
  { wert: 'gestern', schluessel: 'yesterday' },
  { wert: '7d', schluessel: 'days7' },
  { wert: '28d', schluessel: 'days28' },
  { wert: '90d', schluessel: 'days90' },
  { wert: '12m', schluessel: 'months12' },
];

const MERKMALE: { wert: StatMerkmalArt; schluessel: string }[] = [
  { wert: 'VERWEISER', schluessel: 'referrer' },
  { wert: 'KAMPAGNE', schluessel: 'campaign' },
  { wert: 'QUELLE', schluessel: 'source' },
  { wert: 'LAND', schluessel: 'country' },
  { wert: 'GERAET', schluessel: 'device' },
  { wert: 'BROWSER', schluessel: 'browser' },
  { wert: 'SYSTEM', schluessel: 'system' },
];

function dauer(ms: number | null): string {
  if (ms === null) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')} min`;
}

/**
 * Der Vergleich zur Vorperiode.
 *
 * Erlaubt, weil beide Seiten gleich lang und gleich gemessen sind — anders als
 * eine Hochrechnung, die es hier bewusst nicht gibt.
 */
function Veraenderung({ jetzt, vorher, titel }: { jetzt: number; vorher: number; titel: string }) {
  if (vorher === 0) return null;
  const anteil = Math.round(((jetzt - vorher) / vorher) * 100);
  if (anteil === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs text-slate-400">
        <Minus size={11} /> 0 %
      </span>
    );
  }
  const hoch = anteil > 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs ${
        hoch ? 'text-emerald-600' : 'text-slate-500'
      }`}
      title={titel}
    >
      {hoch ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
      {Math.abs(anteil)} %
    </span>
  );
}

function Kennzahl({
  label,
  wert,
  hinweis,
  vergleich,
}: {
  label: string;
  wert: string;
  hinweis?: string;
  vergleich?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-surface p-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs text-slate-500">{label}</span>
        {vergleich}
      </div>
      <div className="mt-0.5 text-xl font-semibold text-slate-900">{wert}</div>
      {hinweis && <div className="mt-0.5 text-[11px] leading-tight text-slate-400">{hinweis}</div>}
    </div>
  );
}

/** Die Lesetiefe einer Seite. Zeichnen tut es `Lesetreppe`. */
function ScrollTreppe({ scroll, titel }: { scroll: StatSeite['scroll']; titel: string }) {
  if (!scroll) return <span className="text-xs text-slate-300">—</span>;
  return (
    <span className="inline-flex items-center gap-1">
      <Lesetreppe
        titel={titel}
        stufen={[
          { marke: '25', anteil: scroll.stufe25 },
          { marke: '50', anteil: scroll.stufe50 },
          { marke: '75', anteil: scroll.stufe75 },
          { marke: '100', anteil: scroll.stufe100 },
        ]}
      />
      <span className="text-[11px] text-slate-400">n={scroll.proben}</span>
    </span>
  );
}

export default function StatistikPanel({
  instanceId,
  stackName,
}: {
  instanceId: string;
  /** Nur fuer den Dateinamen der Ausleitung. */
  stackName?: string | null;
}) {
  const { t } = useTranslation();
  const [zeitraum, setZeitraum] = useState<StatZeitraum>('28d');
  const [merkmal, setMerkmal] = useState<StatMerkmalArt>('VERWEISER');

  const { data: status, isLoading: ladeStatus } = useQuery({
    queryKey: ['statistik', instanceId, 'status'],
    queryFn: () => statistik.status(instanceId),
  });

  const verfuegbar = status?.verfuegbar === true;

  // Erst laden, wenn es etwas zu laden gibt — sonst erzeugt jede
  // ausgeschaltete Webseite drei Abfragen fuer nichts.
  const { data: uebersicht, isLoading } = useQuery({
    queryKey: ['statistik', instanceId, 'uebersicht', zeitraum],
    queryFn: () => statistik.uebersicht(instanceId, zeitraum),
    enabled: verfuegbar,
    refetchOnWindowFocus: false,
  });

  const { data: seiten } = useQuery({
    queryKey: ['statistik', instanceId, 'seiten', zeitraum],
    queryFn: () => statistik.seiten(instanceId, zeitraum),
    enabled: verfuegbar,
    refetchOnWindowFocus: false,
  });

  const { data: merkmalZeilen } = useQuery({
    queryKey: ['statistik', instanceId, 'merkmal', zeitraum, merkmal],
    queryFn: () => statistik.merkmal(instanceId, zeitraum, merkmal),
    enabled: verfuegbar,
    refetchOnWindowFocus: false,
  });

  if (ladeStatus) {
    return <div className="h-32 animate-pulse rounded bg-slate-100" />;
  }

  if (!verfuegbar) {
    // Der Grund im Klartext statt eines leeren Kastens — dieselbe Regel wie bei
    // den Messwerten. Und gleich die Einrichtung dazu: wer hier landet, will
    // meistens genau das tun, wovon der Grund spricht.
    return (
      <div>
        <div className="mb-4 flex gap-2 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
          <Info size={16} className="mt-0.5 shrink-0 text-slate-400" />
          <div>
            <p className="mb-1 font-medium text-slate-700">{t('webstat.noFigures')}</p>
            <p className="text-xs leading-relaxed">{status?.grund}</p>
          </div>
        </div>
        {status?.einbau && <EinbauHinweis einbau={status.einbau} />}
        <StatistikEinrichtung instanceId={instanceId} />
      </div>
    );
  }

  const k: StatKennzahlen | undefined = uebersicht?.jetzt;
  const v: StatKennzahlen | undefined = uebersicht?.vorher;
  const mehrAlsEinTag = (uebersicht?.tage ?? 1) > 1;
  const vergleichTitel = t('webstat.comparedToPrevious');

  return (
    <div>
      {/* Was die Nachschau auf der Webseite ergeben hat — zuerst, weil eine
          Seite, die nicht mehr zaehlt oder den Datenschutzabschnitt verloren
          hat, wichtiger ist als jede Zahl darunter. */}
      {status?.einbau && <EinbauHinweis einbau={status.einbau} />}

      {/* Die Grenze der Messung steht fest im Kopf, nicht in einem Ausklapper. */}
      <div className="mb-4 flex gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
        <Info size={14} className="mt-0.5 shrink-0 text-slate-400" />
        <div>
          <p className="mb-0.5 font-medium text-slate-700">{t('webstat.limitsTitle')}</p>
          <p className="leading-relaxed">{t('webstat.limits')}</p>
        </div>
      </div>


      <div className="mb-4 flex flex-wrap items-center gap-1">
        {ZEITRAEUME.map((z) => (
          <button
            key={z.wert}
            onClick={() => setZeitraum(z.wert)}
            className={`rounded px-2 py-1 text-xs font-medium ${
              zeitraum === z.wert
                ? 'bg-brand-600 text-white'
                : 'border border-slate-300 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {t(`webstat.${z.schluessel}`)}
          </button>
        ))}
        {uebersicht && (
          <span className="ml-1 text-xs text-slate-400">
            {t('webstat.periodRange', { from: uebersicht.von, to: uebersicht.bis })}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="h-24 animate-pulse rounded bg-slate-100" />
      ) : (
        <>
          <div className="mb-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
            <Kennzahl
              label={t('webstat.pageviews')}
              wert={String(k?.aufrufe ?? 0)}
              vergleich={
                <Veraenderung
                  jetzt={k?.aufrufe ?? 0}
                  vorher={v?.aufrufe ?? 0}
                  titel={vergleichTitel}
                />
              }
            />
            <Kennzahl
              label={t('webstat.visits')}
              wert={String(k?.besuche ?? 0)}
              // Die Benennung traegt die Einschraenkung, nicht eine Fussnote.
              hinweis={mehrAlsEinTag ? t('webstat.visitsHint') : undefined}
              vergleich={
                <Veraenderung
                  jetzt={k?.besuche ?? 0}
                  vorher={v?.besuche ?? 0}
                  titel={vergleichTitel}
                />
              }
            />
            <Kennzahl
              label={t('webstat.viewsPerVisit')}
              wert={k?.aufrufeJeBesuch?.toString() ?? '—'}
            />
            <Kennzahl
              label={t('webstat.singlePage')}
              wert={
                k?.einzelseitenAnteil !== null && k?.einzelseitenAnteil !== undefined
                  ? `${k.einzelseitenAnteil} %`
                  : '—'
              }
              hinweis={t('webstat.singlePageHint')}
            />
            <Kennzahl
              label={t('webstat.duration')}
              wert={dauer(k?.dauerSchnittMs ?? null)}
              hinweis={t('webstat.durationHint')}
            />
          </div>

          {/* Was aussortiert wurde — direkt unter den Zahlen, auf die es sich
              bezieht. Ohne diese Angabe ist nicht zu unterscheiden, ob wenig
              los war oder ob die Ausschlussliste greift. */}
          {(k?.botsVerworfen ?? 0) + (k?.adressenVerworfen ?? 0) > 0 && (
            <p className="-mt-3 mb-5 text-[11px] text-slate-400">
              {k!.botsVerworfen > 0 && k!.adressenVerworfen > 0
                ? t('webstat.notCounted', {
                    bots: k!.botsVerworfen,
                    addresses: k!.adressenVerworfen,
                  })
                : k!.adressenVerworfen > 0
                  ? t('webstat.notCountedAddresses', { addresses: k!.adressenVerworfen })
                  : t('webstat.notCountedBots', { bots: k!.botsVerworfen })}
            </p>
          )}

          {/* Zwei Bilder, nicht zwei Achsen in einem: Aufrufe liegen um das
              Drei- bis Fuenffache ueber den Besuchen, und zwei uebereinander
              gelegte Skalen erfinden einen Zusammenhang, den es nicht gibt. */}
          {uebersicht && uebersicht.verlauf.length > 1 && (
            <div className="mb-6 grid gap-4 lg:grid-cols-2">
              <Tagesverlauf
                titel={t('webstat.pageviews')}
                einheit={t('webstat.pageviews')}
                punkte={uebersicht.verlauf.map((v) => ({ tag: v.tag, wert: v.aufrufe }))}
              />
              <Tagesverlauf
                titel={t('webstat.visits')}
                einheit={t('webstat.visits')}
                punkte={uebersicht.verlauf.map((v) => ({ tag: v.tag, wert: v.besuche }))}
              />
            </div>
          )}

          <h3 className="mb-2 text-sm font-semibold text-slate-700">{t('webstat.pages')}</h3>
          {seiten && seiten.length > 0 ? (
            <div className="mb-6 overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs text-slate-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('webstat.page')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('webstat.pageviews')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('webstat.visits')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('webstat.entries')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('webstat.exits')}</th>
                    <th className="px-3 py-2 font-medium">{t('webstat.readTo')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('webstat.duration')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {seiten.map((s) => (
                    <tr key={s.pfad} className="hover:bg-slate-50">
                      <td className="px-3 py-2">
                        <span className="font-mono text-xs text-slate-700">{s.pfad}</span>
                        {s.titel && <div className="text-[11px] text-slate-400">{s.titel}</div>}
                        {s.passtInsFenster && (
                          <span
                            className="mt-0.5 inline-block rounded bg-slate-100 px-1 text-[10px] text-slate-500"
                            title={t('webstat.fitsViewportHint')}
                          >
                            {t('webstat.fitsViewport')}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{s.aufrufe}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                        {s.besuche}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                        {s.einstiege}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                        {s.ausstiege}
                        {s.ausstiegeMitAktion > 0 && (
                          <span
                            className="ml-1 text-[11px] text-emerald-600"
                            title={t('webstat.withActionHint')}
                          >
                            ({t('webstat.withAction', { count: s.ausstiegeMitAktion })})
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <ScrollTreppe
                          scroll={s.scroll}
                          titel={t('webstat.scrollHint', { count: s.scroll?.proben ?? 0 })}
                        />
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                        {dauer(s.dauerSchnittMs)}
                        {s.dauerProben > 0 && s.dauerProben < s.aufrufe && (
                          <div
                            className="text-[10px] text-slate-400"
                            title={t('webstat.measuredHint')}
                          >
                            {t('webstat.measuredOn', { measured: s.dauerProben, total: s.aufrufe })}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mb-6 text-sm text-slate-400">{t('webstat.noPages')}</p>
          )}

          <div className="mb-2 flex flex-wrap items-center gap-1">
            <h3 className="mr-2 text-sm font-semibold text-slate-700">
              {t('webstat.originAndTech')}
            </h3>
            {MERKMALE.map((m) => (
              <button
                key={m.wert}
                onClick={() => setMerkmal(m.wert)}
                className={`rounded px-2 py-0.5 text-xs ${
                  merkmal === m.wert
                    ? 'bg-slate-700 text-white'
                    : 'border border-slate-300 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {t(`webstat.${m.schluessel}`)}
              </button>
            ))}
          </div>

          {merkmalZeilen && merkmalZeilen.length > 0 ? (
            <div className="rounded-lg border border-slate-200 p-3">
              <Rangbalken
                wertLabel={t('webstat.visits')}
                nebenLabel={t('webstat.pageviews')}
                zeilen={merkmalZeilen.map((z) => ({
                  name: z.wert,
                  wert: z.besuche,
                  neben: z.aufrufe,
                }))}
              />
            </div>
          ) : (
            <p className="text-sm text-slate-400">{t('webstat.noValues')}</p>
          )}

          <StatistikAbgleich instanceId={instanceId} zeitraum={zeitraum} />

          <StatistikExport
            instanceId={instanceId}
            stackName={stackName}
            zeitraum={zeitraum}
          />

          <div className="mt-6">
            <StatistikEinrichtung instanceId={instanceId} />
          </div>
        </>
      )}
    </div>
  );
}
